import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {StyleSheet} from "aphrodite";
import gql from "graphql-tag";
import {compose} from "mu-lambda";
import * as React from "react";
import {Redirect} from "react-router-dom";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body, HeadingSmall, Title} from "@khanacademy/wonder-blocks-typography";
import type {ApolloGqlClient, ApolloResult} from "@khan/data-gql-apollo";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {Errors, KAError} from "@khan/logging";

import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "#data-access/gql/with-apollo.tsx";
import PageDisplay from "#data-access/page-display.tsx";
import withLocation, {
    type WithLocationProps,
    type WithoutLocation,
} from "#data-access/with-location.tsx";

const errorToMessageKey = {
    EMAIL_ALREADY_LINKED_TO_YOUR_ACCOUNT:
        "email_already_linked_to_your_account",
    EMAIL_ALREADY_LINKED_TO_ANOTHER_ACCOUNT:
        "email_already_linked_to_another_account",
} as const;

type OwnProps = Empty;

type Props = OwnProps & WithLocationProps & WithApolloProps;

type State = {
    token: string | null | undefined;
    email: string | null | undefined;
    isEmancipated: boolean | null | undefined;
    redirectMessageKey: string | null | undefined;
    isLoading: boolean;
};

class LinkEmail extends React.Component<Props, State> {
    // @ts-expect-error - TS2564 - Property '_mounted' has no initializer and is not definitely assigned in the constructor.
    _mounted: boolean;
    state: State = {
        token: this.props.location.queryParams.token,
        email: null,
        isEmancipated: null,
        redirectMessageKey: null,
        isLoading: false,
    };

    componentDidMount(): void {
        this._mounted = true;
        const {gqlClient} = this.props;
        const {token} = this.state;
        if (token) {
            getEmailFromToken(gqlClient, token)
                .then((result) => {
                    if (!result.data?.getEmailFromLinkAccountToken) {
                        throw new KAError(
                            "Failed to get email from token",
                            Errors.Internal,
                        );
                    }
                    const {email, isEmancipated, error} =
                        result.data.getEmailFromLinkAccountToken;
                    this.safeSetState({email, isEmancipated});

                    if (error) {
                        const redirectMessageKey =
                            errorToMessageKey[error.code] ||
                            "link_account_error";
                        this.safeSetState({redirectMessageKey});
                    }
                })
                .catch(() => {
                    this.safeSetState({
                        redirectMessageKey: "link_account_error",
                    });
                });
        }
    }

    componentWillUnmount(): void {
        this._mounted = false;
    }

    safeSetState(newState: Partial<State>, callback?: () => void): void {
        if (!this._mounted) {
            return;
        }
        // @ts-expect-error - TS2345 - Argument of type 'Partial<State>' is not assignable to parameter of type 'State | ((prevState: Readonly<State>, props: Readonly<Props>) => State | Pick<State, keyof State> | null) | Pick<...> | null'.
        this.setState(newState, callback);
    }

    // @ts-expect-error - TS2322 - Type '() => Promise<void> | undefined' is not assignable to type '() => Promise<undefined> | null | undefined'.
    linkEmail: () => Promise<undefined> | null | undefined = () => {
        const {gqlClient} = this.props;
        const {token} = this.state;
        if (token) {
            this.safeSetState({isLoading: true});
            return linkEmailByToken(gqlClient, token)
                .then((result) => {
                    if (result.data?.verifyLinkedEmail?.email) {
                        this.safeSetState({
                            redirectMessageKey:
                                "successfully_linked_email_to_account",
                        });
                    }
                })
                .catch(() => {
                    this.safeSetState({
                        redirectMessageKey: "link_account_error",
                    });
                });
        }
    };

    renderLinkEmailContent(email: string): React.ReactNode {
        const {isEmancipated, isLoading} = this.state;
        return (
            <React.Fragment>
                <Body>
                    <Trans>
                        Do you want to link <b>{email}</b> to your Khan Academy
                        account?
                    </Trans>
                </Body>
                <Strut size={spacing.xSmall_8} />
                <Body>
                    {isEmancipated
                        ? t`You will be unlinked from your parent's account and be able to log into your account using your Khan Academy password or any Google or Facebook account connected to that email address.`
                        : t`You will be able to log into your account using your Khan Academy password or any Google or Facebook account connected to that email address.`}
                </Body>
                <Strut size={spacing.xSmall_8} />
                {isEmancipated && (
                    <React.Fragment>
                        <Body>
                            <Trans>
                                By clicking "Link this email address", you agree
                                to our{" "}
                                <Link
                                    href="/about/docs/khan-academy-terms-of-service"
                                    target="_blank"
                                >
                                    Terms of Service
                                </Link>{" "}
                                and{" "}
                                <Link
                                    href="/about/privacy-policy"
                                    target="_blank"
                                >
                                    Privacy Policy
                                </Link>
                                .
                            </Trans>
                        </Body>
                        <Strut size={spacing.xSmall_8} />
                    </React.Fragment>
                )}
                <Strut size={spacing.medium_16} />
                <View style={styles.row}>
                    <Button href="/settings/account" kind="secondary">
                        {t`Cancel`}
                    </Button>
                    <Strut size={spacing.xSmall_8} />
                    <Button onClick={this.linkEmail} spinner={isLoading}>
                        {t`Link this email address`}
                    </Button>
                </View>
            </React.Fragment>
        );
    }

    render(): React.ReactNode {
        const {email, redirectMessageKey, token} = this.state;
        if (redirectMessageKey || !token) {
            return (
                <Redirect
                    to={{
                        pathname: "/settings/account",
                        search: `?messageKey=${
                            redirectMessageKey || "link_account_error"
                        }`,
                        state: {email: email},
                    }}
                />
            );
        }
        return (
            <PageDisplay settings={{title: t`Settings`}}>
                <View style={styles.settingsContainer}>
                    <Title>{t`Settings`}</Title>
                    <Strut size={spacing.large_24} />

                    <HeadingSmall style={styles.header} tag="h2">
                        {t`Link an email address`}
                    </HeadingSmall>
                    <Strut size={spacing.medium_16} />
                    {email && this.renderLinkEmailContent(email)}
                    {!email && <CircularSpinner size="medium" />}
                </View>
            </PageDisplay>
        );
    }
}

type GetEmailFromTokenData = {
    getEmailFromLinkAccountToken:
        | {
              email: string;
              isEmancipated: boolean;
              error:
                  | {
                        code: string;
                    }
                  | null
                  | undefined;
          }
        | null
        | undefined;
};

type GetEmailFromTokenVariables = {
    token: string;
};

export const GET_EMAIL_FROM_TOKEN_MUTATION: GqlOperation<
    GetEmailFromTokenData,
    GetEmailFromTokenVariables
> = gqlOp(gql`
    mutation GetEmailFromToken($token: String!) {
        getEmailFromLinkAccountToken(token: $token) {
            email
            isEmancipated
            error {
                code
            }
        }
    }
`);

function getEmailFromToken(
    gqlClient: ApolloGqlClient,
    token: string,
): Promise<ApolloResult<GetEmailFromTokenData>> {
    return gqlClient
        .mutate(GET_EMAIL_FROM_TOKEN_MUTATION, {
            variables: {token},
        })
        .catch((e) => {
            throw new KAError(
                "Failed to get email from token",
                Errors.Internal,
                {cause: e},
            );
        });
}

type LinkEmailByTokenData = {
    verifyLinkedEmail:
        | {
              email: string | null | undefined;
              error:
                  | {
                        code: string;
                    }
                  | null
                  | undefined;
          }
        | null
        | undefined;
};

type LinkEmailByTokenVariables = {
    token: string;
};

const LINK_EMAIL_BY_TOKEN: GqlOperation<
    LinkEmailByTokenData,
    LinkEmailByTokenVariables
> = gqlOp(gql`
    mutation LinkEmailByToken($token: String!) {
        verifyLinkedEmail(token: $token) {
            email
            error {
                code
            }
        }
    }
`);

function linkEmailByToken(
    gqlClient: ApolloGqlClient,
    token: string,
): Promise<ApolloResult<LinkEmailByTokenData>> {
    return gqlClient
        .mutate(LINK_EMAIL_BY_TOKEN, {
            variables: {token},
        })
        .then((r) => {
            const error = r.data?.verifyLinkedEmail?.error;
            if (error != null) {
                throw new KAError(error.code, Errors.Internal);
            }

            return r;
        })
        .catch((e) => {
            throw new KAError(
                "Failed to link email by token",
                Errors.Internal,
                {cause: e},
            );
        });
}

const styles = StyleSheet.create({
    header: {
        color: color.offBlack,
    },
    settingsContainer: {
        marginTop: spacing.xxLarge_48,
        marginBottom: spacing.xLarge_32,
        marginLeft: "auto",
        marginRight: "auto",
        width: 640,
        paddingLeft: spacing.medium_16,
    },
    row: {
        flexDirection: "row",
        justifyContent: "flex-end",
    },
});

type ExportProps = WithoutApollo<
    WithoutLocation<
        JSX.LibraryManagedAttributes<
            typeof LinkEmail,
            React.ComponentProps<typeof LinkEmail>
        >
    >
>;

export default compose(
    withLocation,
    withApollo,
)(LinkEmail) as React.ComponentType<ExportProps>;
